<template>
  <!-- EN-1516: change the date range of a queued, unsent download. -->
  <q-dialog
    :model-value="modelValue"
    persistent
    @update:model-value="(v) => $emit('update:modelValue', v)"
    @show="onShow"
  >
    <q-card style="width: 480px; max-width: 90vw">
      <q-toolbar>
        <q-toolbar-title class="text-blue-brand text-bold"
          >Edit Download Range</q-toolbar-title
        >
        <q-btn
          flat
          round
          dense
          icon="close"
          :disable="isSaving"
          @click="close"
        />
      </q-toolbar>

      <q-card-section v-if="command" class="q-pt-none">
        <div class="text-caption text-grey-8 q-mb-sm">
          AGM UID {{ command.uid }} · {{ typeLabel }} · CID
          {{ command.commandId }}
        </div>

        <!-- Start date/time -->
        <q-input
          v-model="startDate"
          dense
          filled
          label="Start Date (UTC)"
          class="q-mb-sm"
          data-test="queued-range-start"
        >
          <template #prepend>
            <q-icon name="event" class="cursor-pointer">
              <q-popup-proxy
                ref="qStartDateProxy"
                transition-show="scale"
                transition-hide="scale"
              >
                <q-date
                  v-model="startDate"
                  mask="YYYY-MM-DD HH:mm:ss"
                  @update:model-value="hideProxy('qStartDateProxy')"
                />
              </q-popup-proxy>
            </q-icon>
          </template>
          <template #append>
            <q-icon name="access_time" class="cursor-pointer">
              <q-popup-proxy
                ref="qStartTimeProxy"
                transition-show="scale"
                transition-hide="scale"
              >
                <q-time
                  v-model="startDate"
                  mask="YYYY-MM-DD HH:mm:ss"
                  format24h
                  with-seconds
                  @update:model-value="hideProxy('qStartTimeProxy')"
                />
              </q-popup-proxy>
            </q-icon>
          </template>
        </q-input>

        <!-- End date/time -->
        <q-input
          v-model="endDate"
          dense
          filled
          label="End Date (UTC)"
          data-test="queued-range-end"
        >
          <template #prepend>
            <q-icon name="event" class="cursor-pointer">
              <q-popup-proxy
                ref="qEndDateProxy"
                transition-show="scale"
                transition-hide="scale"
              >
                <q-date
                  v-model="endDate"
                  mask="YYYY-MM-DD HH:mm:ss"
                  @update:model-value="hideProxy('qEndDateProxy')"
                />
              </q-popup-proxy>
            </q-icon>
          </template>
          <template #append>
            <q-icon name="access_time" class="cursor-pointer">
              <q-popup-proxy
                ref="qEndTimeProxy"
                transition-show="scale"
                transition-hide="scale"
              >
                <q-time
                  v-model="endDate"
                  mask="YYYY-MM-DD HH:mm:ss"
                  format24h
                  with-seconds
                  @update:model-value="hideProxy('qEndTimeProxy')"
                />
              </q-popup-proxy>
            </q-icon>
          </template>
        </q-input>

        <div v-if="isRaw" class="text-caption text-grey-7 q-mt-xs">
          Raw downloads are limited to {{ rawCapLabel }}.
        </div>

        <q-banner
          v-if="errorMessage"
          dense
          rounded
          class="bg-red-1 text-negative q-mt-sm"
          data-test="queued-range-error"
        >
          {{ errorMessage }}
        </q-banner>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat no-caps label="Cancel" :disable="isSaving" @click="close" />
        <q-btn
          class="btn-brand"
          no-caps
          label="Save"
          :loading="isSaving"
          :disable="!command"
          data-test="queued-range-save"
          @click="save"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import CommandService from '@/services/CommandService';
import { hidePopupProxy } from '@/utils/QuasarProxyHelper';
import { DOWNLOAD_OPTION_RAW } from '@/utils/DownloadRangeDefaults';
import { IQueuedCommand } from '@/models/QueuedCommand';
import {
  apiUtcToPicker,
  buildRangeUpdateRequest,
  editErrorMessage,
  queuedCommandFileType,
  queuedCommandTypeLabel,
  rawMaxRangeSeconds,
  validateQueuedRange,
} from '@/utils/QueuedCommandDisplay';

export default defineComponent({
  name: 'EditQueuedRangeDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    command: {
      type: Object as PropType<IQueuedCommand | null>,
      default: null,
    },
    /** Device firmware (e.g. 10.71), for the RAW cap; undefined = unknown. */
    firmwareVersion: {
      type: Number as PropType<number | undefined>,
      default: undefined,
    },
  },
  emits: ['update:modelValue', 'saved', 'stale'],
  data() {
    return {
      startDate: '',
      endDate: '',
      errorMessage: '',
      isSaving: false,
    };
  },
  computed: {
    typeLabel(): string {
      return this.command ? queuedCommandTypeLabel(this.command) : '';
    },
    isRaw(): boolean {
      return (
        !!this.command &&
        queuedCommandFileType(this.command) === DOWNLOAD_OPTION_RAW
      );
    },
    rawCapLabel(): string {
      const seconds = rawMaxRangeSeconds(this.firmwareVersion);
      return seconds % 60 === 0 ? seconds / 60 + ' minutes' : seconds + ' s';
    },
  },
  methods: {
    onShow() {
      this.errorMessage = '';
      this.isSaving = false;
      this.startDate = apiUtcToPicker(
        this.command?.parameters?.FileNamesStartDate
      );
      this.endDate = apiUtcToPicker(this.command?.parameters?.FileNamesEndDate);
    },
    hideProxy(refName: string) {
      hidePopupProxy(this, refName);
    },
    close() {
      this.$emit('update:modelValue', false);
    },
    async save() {
      if (!this.command) return;
      this.errorMessage = '';
      const validation = validateQueuedRange(
        this.startDate,
        this.endDate,
        queuedCommandFileType(this.command),
        this.firmwareVersion
      );
      if (validation.error || !validation.start || !validation.end) {
        this.errorMessage = validation.error || 'The range is not valid.';
        return;
      }
      this.isSaving = true;
      try {
        const response = await CommandService.updateCommandRange(
          this.command.commandId,
          buildRangeUpdateRequest(validation.start, validation.end)
        );
        this.$emit('saved', response.data);
        this.close();
      } catch (exception: any) {
        const status = exception?.response?.status;
        this.errorMessage = editErrorMessage(status, exception?.response?.data);
        // The row changed under us (sent, processed, device online): let the
        // list refresh so it shows the current state.
        if (status === 404 || status === 409) this.$emit('stale');
      } finally {
        this.isSaving = false;
      }
    },
  },
});
</script>
